/**
 * useAuthForm Hook
 * Custom hook for authentication forms using react-hook-form and Zod validation
 * Provides consistent form handling, validation, and error management
 */

import { zodResolver } from "@hookform/resolvers/zod"
import * as Sentry from "@sentry/react"
import type { ApiError } from "@shared-types/api.types"
import { type AxiosError, isAxiosError } from "axios"
import { useState } from "react"
import { type DefaultValues, useForm } from "react-hook-form"
import { toast } from "sonner"
import type { z } from "zod"
import { getFieldErrors, transformApiError } from "@/lib/errorTransformer"

/**
 * Decide whether a submit failure should be reported to Sentry. Expected client
 * failures (wrong credentials, validation) are shown to the user inline and are
 * NOT exceptions. We report genuinely unexpected failures: 5xx, network /
 * no-response, non-Axios bugs, and a 400/422 with NO structured field errors
 * (usually a client<->server contract bug rather than user input).
 */
const isUnexpectedError = (error: unknown, hadFieldErrors: boolean): boolean => {
  if (!isAxiosError(error)) return true // a non-Axios error is a real bug
  const status = error.response?.status
  if (status === undefined) return true // network error / no response
  if (status >= 500) return true
  // Other 4xx (401 wrong credentials, 403 banned, 404, 409 conflict, 429) are
  // expected and surfaced inline -- suppressing them is the point of #529.
  return !hadFieldErrors && (status === 400 || status === 422)
}

/**
 * Custom hook for authentication forms
 *
 * @example
 * ```tsx
 * const { form, handleSubmit, isSubmitting, serverError } = useAuthForm(
 *   loginSchema,
 *   async (data) => {
 *     await login(data.email, data.password)
 *     navigate('/dashboard')
 *   }
 * )
 * ```
 */
export function useAuthForm<T extends Record<string, unknown>>(
  // biome-ignore lint/suspicious/noExplicitAny: Required for zodResolver compatibility
  schema: z.ZodType<T, any, any>,
  onSubmit: (data: T) => Promise<void>,
  options?: {
    skipValidationFields?: string[]
  },
) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [serverError, setServerError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string> | null>(null)

  const form = useForm({
    resolver: zodResolver(schema),
    mode: "onSubmit", // Validate only on form submit
    defaultValues: {
      countryCode: "",
      phoneNumber: "",
      streetAddress: "",
      city: "",
      state: "",
      zipcode: "",
      country: "",
    } as unknown as DefaultValues<T>,
  })

  const handleSubmit = async (e?: React.BaseSyntheticEvent) => {
    if (e) {
      e.preventDefault()
    }

    setIsSubmitting(true)
    setServerError(null)
    setFieldErrors(null)

    try {
      // Small delay to ensure form values are properly synchronized
      // Addresses timing issue when submitting via Enter key
      await new Promise(resolve => setTimeout(resolve, 10))
      // Validate with react-hook-form (optionally skip certain fields)
      const fieldsToValidate = options?.skipValidationFields
        ? Object.keys(form.getValues()).filter(
            field => !options.skipValidationFields?.includes(field),
          )
        : undefined
      const isValid = fieldsToValidate ? await form.trigger(fieldsToValidate) : await form.trigger()
      if (!isValid) {
        setIsSubmitting(false)
        return
      }

      // Get validated values
      const values = form.getValues()

      // Call submit handler
      await onSubmit(values as T)
    } catch (error) {
      // Extract field-level errors
      const err = error as AxiosError<ApiError>
      const errors = getFieldErrors(err)
      if (errors) {
        setFieldErrors(errors)
        // Also set individual form errors
        for (const [field, message] of Object.entries(errors)) {
          form.setError(field as string, { type: "server", message })
        }
      }
      // Transform error to user-friendly message
      const errorMessage = transformApiError(err)
      setServerError(errorMessage)
      // Show toast for non-field errors (e.g. banned email, account suspended)
      if (!errors) {
        toast.error(errorMessage)
      }
      // The error is fully handled inline above. Don't re-throw: handleSubmit is
      // wired as a form onSubmit, so a rejection here becomes an UNHANDLED promise
      // rejection that Sentry reports (issue #529 -- the /login 401 noise). Report
      // only genuinely unexpected failures explicitly, with context.
      if (isUnexpectedError(error, Boolean(errors))) {
        Sentry.captureException(error, {
          tags: { feature: "auth-form" },
          extra: {
            status: isAxiosError(error) ? error.response?.status : undefined,
            url: isAxiosError(error) ? error.config?.url : undefined,
          },
        })
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  return {
    form,
    handleSubmit,
    isSubmitting,
    serverError,
    fieldErrors,
    clearServerError: () => setServerError(null),
    clearFieldErrors: () => setFieldErrors(null),
  }
}
